import { createListenerMiddleware } from '@reduxjs/toolkit'
import { AppDispatch, RootState } from '@/store/store'
import { kConversationListCacheDuration, kConversationRefreshCacheDuration } from '@/chat-common/conversation_list/constants'
import { getMatterConversationList } from '../fetch/get-matter-conversation-list'
import { AgentConversation, AgentConversationsActions } from './slice'
import { getAgentConversationData } from '../fetch/get-agent-conversation-data'
import { AgentEventsActions } from '@/agent/events/store/slice'
import { ArtifactsActions, ArtifactVersionTurnIdMap } from '@/artifacts/store/slice'
import { hydrateConversationTemplateState } from '@/artifact-templates/utils/hydrate-conversation-template-state'
import { hydrateConversationUserFileRecords } from '@/user_files/files/utils/hydrate-conversation-user-files'
import { AgentResponseMetadataActions } from '@/agent/response_metadata/store/slice'
import { AgentConversationFormActions } from '@/agent/chat_form/store/slice'
import { mapErrorToConversationLoadError } from '@/agent/conversations/util/conversation-load-error-mapper'
import patchGenerateAgentConversationTitle from '../fetch/patch-generate-agent-conversation-title'
import { ArtifactVersionStatusType } from '@/artifacts/schemas'
import postGenerateConversationFollowUpQuestions from '../fetch/post-generate-conversation-follow-up-questions'
import { nanoid } from 'nanoid'
import { AgentReferencesActions } from '@/agent/references/store/slice'
import { InlineEditActions } from '@/inline-edit/store/slice'
import { selectConversationWebSocketConnected } from '@/websocket_v2/ws2.selectors'
import { selectAgentConversationFromId, selectAgentConversationMatterId } from './selectors'

export const agentConversationsListenerMiddleware = createListenerMiddleware()
const startListener = agentConversationsListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()

/**
 * Fetch Matter Conversations List Action Listener
 * Fetches one matter's conversation list, gated by that matter's cache duration and guarded by a
 * per-matter request id so a slow refresh can't overwrite fresher data or clobber a just-created
 * conversation.
 */
startListener({
  actionCreator: AgentConversationsActions.refreshMatterConversationList,
  effect: async (action, listenerApi) => {
    const { matterId, forceRefresh } = action.payload
    // No matter (e.g. still hydrating, or a conversation with no matter) means nothing to refresh.
    if (!matterId) return

    const entry = listenerApi.getState().agentConversationsState.conversationListByMatterId[matterId]

    // Skip a fetch already in flight, unless forced.
    if (!forceRefresh && entry?.loading) return
    // Skip while this matter's cached list is still fresh, unless forced.
    if (!forceRefresh && entry?.lastRefresh != null && Date.now() - entry.lastRefresh < kConversationListCacheDuration) return

    const requestId = (entry?.requestId ?? 0) + 1
    listenerApi.dispatch(AgentConversationsActions.fetchMatterConversationsStarted({ matterId, requestId }))

    try {
      const response = await getMatterConversationList(matterId)

      // Sort by updated_at, most recent first (milliseconds since epoch).
      const sorted = [...response.conversations].sort((a, b) => b.updated_at - a.updated_at)

      listenerApi.dispatch(
        AgentConversationsActions.syncMatterConversationsFromReadArray({ matterId, conversations: sorted, requestId, lastRefresh: Date.now() })
      )
    } catch {
      listenerApi.dispatch(AgentConversationsActions.fetchMatterConversationsFailed({ matterId, requestId }))
    }
  },
})

/**
 * Fetch Conversation Data Action Listener
 * This listener fetches the conversation data from the server,
 * and dispatches data to the appropriate slices.
 */
startListener({
  actionCreator: AgentConversationsActions.fetchConversationData,
  effect: async (action, listenerApi) => {
    const { conversationId, forceRefresh } = action.payload

    // Get the existing state
    const state = listenerApi.getState()
    const currentConversationState: AgentConversation | null = selectAgentConversationFromId(state, { conversationId })

    // Is loading already? i.e. the conversation is already being fetched
    const isLoading = currentConversationState?.isLoading ?? false

    // If the web socket is connected, conversation data is already being received via the web socket
    const isWebSocketConnected = selectConversationWebSocketConnected(state, { conversationId })

    // Cache Handling
    const cacheDuration = kConversationRefreshCacheDuration
    const lastFetch = currentConversationState?.lastRefresh ?? null
    const cacheExpired = lastFetch === null || Date.now() - lastFetch > cacheDuration

    // Return early if:
    // - the cache has not expired and we are not forcing a refresh
    // - the conversation is already being fetched
    // - conversation data is already being received via the web socket
    if ((!cacheExpired && !forceRefresh) || isLoading || isWebSocketConnected) {
      return
    }

    try {
      listenerApi.dispatch(AgentConversationsActions.setConversationError({ conversationId, loadingError: null }))
      listenerApi.dispatch(AgentConversationsActions.setConversationLoading({ conversationId, isLoading: true }))

      // Fetch conversation data
      const data = await getAgentConversationData(conversationId)

      // Upsert the conversation
      listenerApi.dispatch(AgentConversationsActions.upsertConversationFromRead(data.conversation))

      // Upsert events
      if (data.events && data.events.length > 0) {
        listenerApi.dispatch(AgentEventsActions.upsertEvents({ conversationId, events: data.events }))
      }

      // Upsert artifact_versions
      if (data.artifact_versions.length > 0) {
        listenerApi.dispatch(ArtifactsActions.upsertArtifactVersions(data.artifact_versions))

        // Maps artifact versions to the turns that produced them. Enables downstream artifact-dependents to access turn-content (e.g. references).
        const turnIdMap: ArtifactVersionTurnIdMap = {}
        for (const event of data.events) {
          if (event.artifact_version_id) turnIdMap[event.artifact_version_id] = event.turn_id
        }
        listenerApi.dispatch(ArtifactsActions.setArtifactVersionTurnIds(turnIdMap))

        // Identify generating artifacts and poll for updates
        const generatingArtifactVersions = data.artifact_versions.filter((artifactVersion) => artifactVersion.status === ArtifactVersionStatusType.GENERATING)
        if (generatingArtifactVersions.length > 0) {
          generatingArtifactVersions.forEach((artifactVersion) => {
            listenerApi.dispatch(ArtifactsActions.startArtifactVersionStatusPoll({ artifactVersionId: artifactVersion.id }))
          })
        }
      }

      // Merge the conversation's persisted template state (selections, applied records, and the
      // referenced template files so their names resolve in the toolbar).
      hydrateConversationTemplateState(data.template_selections, data.applied_templates, listenerApi.dispatch)

      // Upsert response_metadata
      if (data.response_metadata && data.response_metadata.length > 0) {
        listenerApi.dispatch(AgentResponseMetadataActions.upsertResponseMetadataList(data.response_metadata))
      }

      // Upsert the references
      if (data.references && data.references.length > 0) {
        listenerApi.dispatch(AgentReferencesActions.upsertReferences({ references: data.references }))
      }

      // Set the latest request params in the chat form
      if (data.latest_request_params) {
        // Restore all request params directly (includes file paths, template paths, and filter settings)
        listenerApi.dispatch(
          AgentConversationFormActions.restoreRequestParams({
            conversationId,
            requestParams: data.latest_request_params,
          })
        )

        // Eagerly resolve the attached user-file + writing-sample records so the (collapsed)
        // selected-files header can show their real processing status without waiting for the
        // panel to expand (the panel is otherwise the only place these records get loaded).
        hydrateConversationUserFileRecords(data.latest_request_params, listenerApi.dispatch)
      }

      listenerApi.dispatch(AgentConversationsActions.setConversationLastRefresh({ conversationId, lastRefresh: Date.now() }))
    } catch (error) {
      const loadingError = mapErrorToConversationLoadError(error, conversationId)
      listenerApi.dispatch(AgentConversationsActions.setConversationError({ conversationId, loadingError }))
    } finally {
      listenerApi.dispatch(AgentConversationsActions.setConversationLoading({ conversationId, isLoading: false }))
    }
  },
})

/**
 * Generate Conversation Title Action Listener
 * This listener generates the conversation title for a new conversation
 */
startListener({
  actionCreator: AgentConversationsActions.generateConversationTitle,
  effect: async (action, listenerApi) => {
    const { conversationId } = action.payload
    try {
      await patchGenerateAgentConversationTitle(conversationId)

      const matterId = selectAgentConversationMatterId(listenerApi.getState(), { conversationId })
      listenerApi.dispatch(AgentConversationsActions.refreshMatterConversationList({ matterId, forceRefresh: true }))
    } catch (error) {
      console.error('Failed to generate conversation title', error)
    }
  },
})

/**
 * Generate Conversation Follow Up Questions Action Listener
 * This listener creates the follow up questions for a conversation
 */
startListener({
  actionCreator: AgentConversationsActions.generateConversationFollowUpQuestions,
  effect: async (action, listenerApi) => {
    const { conversationId } = action.payload
    const newEventId = nanoid()

    try {
      // Create loading follow-up questions event
      listenerApi.dispatch(AgentEventsActions.insertFollowUpQuestionsEvent({ conversationId, newEventId }))

      // Fetch follow-up questions
      const response = await postGenerateConversationFollowUpQuestions(conversationId)

      // Update event with suggestions
      listenerApi.dispatch(
        AgentEventsActions.updateFollowUpQuestionsState({
          conversationId,
          eventId: newEventId,
          loading: false,
          followUpQuestions: response.questions,
        })
      )
    } catch (error) {
      console.error('Failed to generate conversation follow up questions', error)
      listenerApi.dispatch(
        AgentEventsActions.updateFollowUpQuestionsState({
          conversationId,
          eventId: newEventId,
          loading: false,
          isError: true,
        })
      )
    }
  },
})

/**
 * Upsert references returned by an inline edit response.
 * The BE persists these regardless of whether the user accepts the diff,
 * so the store must reflect that — keeps the chat references list and the
 * artifact editor badges in sync with the server.
 */
startListener({
  actionCreator: InlineEditActions.setPendingReplacement,
  effect: (action, listenerApi) => {
    const { references } = action.payload.replacement
    if (references && references.length > 0) {
      listenerApi.dispatch(AgentReferencesActions.upsertReferences({ references }))
    }
  },
})
